import type { EditorJsData } from './editorjs-types';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import './editor.scss';
import { useDocumentMermaidVisibility } from './document-mermaid-visibility-context';
import AddToChatTune from './editorjs-add-to-chat-tune';
import { SubpagesBlock } from './subpages-block.tsx';

export interface EditorJsEditorHandle {
  getValue: () => EditorJsData | null;
  resetContent: (value: EditorJsData | null) => void;
}

/** Values read at call time by Editor.js tools so the instance is not recreated when props/context change. */
type EditorDynamicConfig = {
  onUpload?: (file: File) => Promise<string>;
  onAddToChat?: (blockId: string) => void;
  isSourceVisible: (entryId: string) => boolean;
  setSourceVisible: (entryId: string, visible: boolean) => void;
};

interface EditDocumentEditorProps {
  /** Initial content as Editor.js data. */
  value?: EditorJsData | null;
  /** Document id for AI processing prompts (undefined for new/unsaved docs). */
  documentId?: string;
  /** Whether this is a new document (not yet saved). */
  isNewDocument: boolean;
  /** Handler for image uploads (drag-drop, paste, or button). Returns the URL. */
  onUpload?: (file: File) => Promise<string>;
  /** Current document path for context-aware tools like Subpages. */
  parentPath: string;
  /** Current child documents for the Subpages block. */
  children: Array<{ id: string; path: string; label?: string }>;
  /** Open a React-powered prompt to create a subpage. */
  onCreateSubpage?: () => void;
  /** Navigate to a document path (client-side). Used by Subpages block links. */
  onNavigateToDocument?: (path: string) => void;
  /** Add a block reference to the document chat composer (block tune). */
  onAddToChat?: (blockId: string) => void;
}

export const EditDocumentEditor = forwardRef<
  EditorJsEditorHandle,
  EditDocumentEditorProps
>(function EditDocumentEditor(
  {
    value,
    onUpload,
    parentPath,
    children,
    onCreateSubpage,
    onNavigateToDocument,
    onAddToChat,
  },
  ref,
) {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<any | null>(null);
  const latestValueRef = useRef<EditorJsData | null>(value ?? null);
  const { isSourceVisible, setSourceVisible } = useDocumentMermaidVisibility();

  const dynamicConfigRef = useRef<EditorDynamicConfig>({
    onUpload,
    onAddToChat,
    isSourceVisible,
    setSourceVisible,
  });
  dynamicConfigRef.current = {
    onUpload,
    onAddToChat,
    isSourceVisible,
    setSourceVisible,
  };

  const [portalTargets, setPortalTargets] = useState<Map<string, HTMLElement>>(
    () => new Map(),
  );

  const registerPortalTarget = useCallback((id: string, el: HTMLElement) => {
    setPortalTargets((prev) => {
      const next = new Map(prev);
      next.set(id, el);
      return next;
    });
  }, []);

  const unregisterPortalTarget = useCallback((id: string) => {
    setPortalTargets((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const subpagesPortalNodes = useMemo(() => {
    return Array.from(portalTargets.entries()).map(([id, el]) =>
      createPortal(
        <SubpagesBlock
          key={id}
          parentPath={parentPath}
          children={children}
          onCreateSubpage={onCreateSubpage}
          onNavigateToDocument={onNavigateToDocument}
        />,
        el,
      ),
    );
  }, [
    portalTargets,
    parentPath,
    children,
    onCreateSubpage,
    onNavigateToDocument,
  ]);

  useEffect(() => {
    let cancelled = false;

    const setup = async () => {
      if (!holderRef.current) return;

      try {
        const [
          { default: EditorJS },
          { default: CodeTool },
          HeaderModule,
          { default: ImageTool },
          { default: EditorjsList },
          { SubpagesTool },
          { default: TableWithImagesTool },
          { default: MermaidTool },
          StrikethroughModule,
        ] = await Promise.all([
          import('@editorjs/editorjs'),
          import('@editorjs/code'),
          import('@editorjs/header'),
          import('@editorjs/image'),
          import('@editorjs/list'),
          import('./editorjs-subpages-tool'),
          import('./editorjs-table-with-images'),
          import('./editorjs-mermaid-tool'),
          import('editorjs-strikethrough'),
        ]);

        if (cancelled || !holderRef.current) return;

        const hasUploader = Boolean(dynamicConfigRef.current.onUpload);

        const headerClass =
          typeof HeaderModule === 'function'
            ? HeaderModule
            : ((HeaderModule as any).default ?? HeaderModule);

        const StrikethroughTool =
          (StrikethroughModule as any)?.default ?? StrikethroughModule;

        // Editor.js strikethrough wraps the provided `Range`. In lists, users
        // often double-click selecting a whole line (including whitespace/
        // newlines). Wrapping those edges can make Editor.js interpret it as
        // additional (empty) list items.
        //
        // Fix: build a new `Range` that starts at the first non-whitespace
        // text node within the original range and ends at the last non-
        // whitespace text node within the original range.
        const trimRangeEdges = (range: Range): Range | null => {
          const ancestor = range.commonAncestorContainer;
          const walkerRoot =
            ancestor.nodeType === 3 ? ancestor.parentNode : ancestor;
          if (!walkerRoot) return null;

          const treeWalker = document.createTreeWalker(
            walkerRoot,
            NodeFilter.SHOW_TEXT,
          );

          const intersectingNonWhitespaceTextNodes: Text[] = [];
          let node = treeWalker.nextNode() as Node | null;
          while (node) {
            const textNode = node as Text;
            if (range.intersectsNode(textNode) && textNode.data?.trim()) {
              intersectingNonWhitespaceTextNodes.push(textNode);
            }
            node = treeWalker.nextNode() as Node | null;
          }

          if (intersectingNonWhitespaceTextNodes.length === 0) return null;

          const first = intersectingNonWhitespaceTextNodes[0];
          const last =
            intersectingNonWhitespaceTextNodes[
              intersectingNonWhitespaceTextNodes.length - 1
            ];

          const firstStartLimit =
            range.startContainer === first ? range.startOffset : 0;
          const lastEndLimit =
            range.endContainer === last ? range.endOffset : last.data.length;

          // Adjust start/end offsets within the first/last text nodes by
          // trimming whitespace characters.
          let startOffset = Math.min(firstStartLimit, first.data.length);
          while (
            startOffset < first.data.length &&
            /\s/.test(first.data[startOffset] ?? '')
          ) {
            startOffset += 1;
          }

          let endOffset = Math.max(0, Math.min(lastEndLimit, last.data.length));
          while (
            endOffset > startOffset &&
            /\s/.test(last.data[endOffset - 1] ?? '')
          ) {
            endOffset -= 1;
          }

          if (endOffset <= startOffset) return null;

          const trimmed = document.createRange();
          trimmed.setStart(first, startOffset);
          trimmed.setEnd(last, endOffset);

          return trimmed.toString().trim() ? trimmed : null;
        };

        class SafeStrikethroughTool extends StrikethroughTool {
          surround(range: Range) {
            const trimmed = trimRangeEdges(range);
            if (!trimmed) return;
            return super.surround(trimmed);
          }
        }

        const inlineToolbarTools: string[] = [
          'link',
          'bold',
          'italic',
          // Editor.js strikethrough tool (wraps with <del class="cdx-strikethrough">).
          'strikethrough',
        ];

        const editor = new EditorJS({
          holder: holderRef.current,
          data: (latestValueRef.current as any) ?? { blocks: [] },
          autofocus: true,
          tools: {
            header: {
              class: headerClass,
              // Enables inline formatting toolbar on double-click/selection.
              inlineToolbar: inlineToolbarTools,
              config: {
                levels: [1, 2, 3],
                defaultLevel: 2,
              },
            },
            list: {
              class: EditorjsList,
              inlineToolbar: inlineToolbarTools,
            },
            strikethrough: {
              class: SafeStrikethroughTool as any,
            },
            code: CodeTool,
            image: {
              class: ImageTool,
              ...(hasUploader
                ? {
                    config: {
                      uploader: {
                        async uploadByFile(file: File) {
                          const upload = dynamicConfigRef.current.onUpload;
                          if (!upload) {
                            return { success: 0 };
                          }
                          const url = await upload(file);
                          return {
                            success: 1,
                            file: {
                              url,
                            },
                          };
                        },
                      },
                    },
                  }
                : {}),
            },
            table: {
              class: TableWithImagesTool,
              config: {
                insertImageButtonText: 'Insert image',
                ...(hasUploader
                  ? {
                      uploader: {
                        async uploadByFile(file: File) {
                          const upload = dynamicConfigRef.current.onUpload;
                          if (!upload) {
                            return { success: 0 };
                          }
                          const url = await upload(file);
                          return {
                            success: 1,
                            file: {
                              url,
                            },
                          };
                        },
                      },
                    }
                  : {}),
              },
            },
            mermaid: {
              class: MermaidTool,
              config: {
                getShowSource: (entryId: string) =>
                  dynamicConfigRef.current.isSourceVisible(entryId),
                setShowSource: (entryId: string, visible: boolean) =>
                  dynamicConfigRef.current.setSourceVisible(entryId, visible),
              },
            },
            subpages: {
              class: SubpagesTool,
              config: {
                registerPortalTarget,
                unregisterPortalTarget,
              },
            },
            addToChatTune: {
              class: AddToChatTune,
              config: {
                onAddToChatRequested: (blockId: string) => {
                  dynamicConfigRef.current.onAddToChat?.(blockId);
                },
              },
            },
          },
          tunes: ['addToChatTune'],
          async onChange(api) {
            const data = (await api.saver.save()) as EditorJsData;
            latestValueRef.current = data;
          },
        });

        editorRef.current = editor;
      } catch {
        if (holderRef.current) {
          holderRef.current.innerText =
            'Failed to load editor. Check console for details.';
        }
      }
    };

    void setup();

    return () => {
      cancelled = true;
      const editor = editorRef.current;
      if (editor) {
        void editor.isReady
          .then(() => {
            editor.destroy();
            editorRef.current = null;
          })
          .catch(() => {
            editorRef.current = null;
          });
      }
      setPortalTargets(new Map());
    };
  }, [registerPortalTarget, unregisterPortalTarget]);

  // When the `value` prop changes (e.g. new document loaded), re-render content
  useEffect(() => {
    if (!editorRef.current) return;
    latestValueRef.current = value ?? { blocks: [] };
    void editorRef.current.isReady
      .then(() =>
        editorRef.current.render(
          (latestValueRef.current as any) ?? { blocks: [] },
        ),
      )
      .catch(() => {});
  }, [value]);

  useImperativeHandle(
    ref,
    () => ({
      getValue: () => latestValueRef.current,
      resetContent: (data: EditorJsData | null) => {
        latestValueRef.current = data ?? { blocks: [] };
        const editor = editorRef.current;
        if (!editor) return;
        void editor.isReady
          .then(() =>
            editor.render((latestValueRef.current as any) ?? { blocks: [] }),
          )
          .catch(() => {});
      },
    }),
    [],
  );

  return (
    <div
      className="edit-document-editor-root"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: '300px',
      }}
    >
      {subpagesPortalNodes}
      <div
        style={{
          flex: 1,
          minHeight: '300px',
          width: '100%',
        }}
      >
        <div
          ref={holderRef}
          style={{
            height: '100%',
            width: '100%',
            minHeight: '300px',
          }}
          className="editorjs-root editorjs-holder"
        />
      </div>
    </div>
  );
});
